import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBomb,
    faCat,
    faChartPie,
    faCode,
} from '@fortawesome/free-solid-svg-icons';
import type { LifeCycles } from 'single-spa';

import { Windows } from './+state/windows/window.model';
import windowStyles from './components/desktop/Window.module.css';
import {
    MICROFRONTEND_REMOTES,
    type MicrofrontendName,
} from './microfrontends.manifest';

export type { MicrofrontendName };

export interface IMicrofrontendDefinition {
    classes?: string[];
    icon: IconDefinition;
    /** Window this microfrontend fills. */
    label: Windows;
    name: MicrofrontendName;
}

export const MICROFRONTENDS: IMicrofrontendDefinition[] = [
    {
        classes: [windowStyles.windowAllowOverflow],
        icon: faCat,
        label: Windows.CV,
        name: 'cv',
    },
    { icon: faBomb, label: Windows.Minesweeper, name: 'minesweeper' },
    { icon: faCode, label: Windows.SelfDocument, name: 'selfdocument' },
    { icon: faChartPie, label: Windows.PieChart, name: 'piechart' },
];

const BY_LABEL = new Map(MICROFRONTENDS.map((it) => [it.label, it]));

export function getMicrofrontendByWindow(
    label: Windows,
): IMicrofrontendDefinition | undefined {
    return BY_LABEL.get(label);
}

/**
 * In production this is the file the microfrontend's own build emitted next to
 * this app. In development it is the entry module of that microfrontend's dev
 * server, which allows the cross origin import.
 */
export function microfrontendUrl(name: MicrofrontendName): string {
    const { entry, origin, source } = MICROFRONTEND_REMOTES[name];

    return import.meta.env.DEV
        ? `${origin}/${source}`
        : `${import.meta.env.BASE_URL}apps/${name}/${entry}`;
}

const loaded = new Map<MicrofrontendName, Promise<LifeCycles>>();

/** Load, at most once per session, the single-spa lifecycles of an app. */
export function loadMicrofrontend(
    name: MicrofrontendName,
): Promise<LifeCycles> {
    let lifecycles = loaded.get(name);
    if (!lifecycles) {
        lifecycles = import(
            /* @vite-ignore */ microfrontendUrl(name)
        ) as Promise<LifeCycles>;
        loaded.set(name, lifecycles);
    }
    return lifecycles;
}
